class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.initUpsellObserver();
  }

  initUpsellObserver() {
    console.log('[AI Upsell] Initializing observer on root element');

    // We observe 'this' (the <cart-drawer> element) because `.drawer__inner` might be replaced entirely
    // or its content updated. By observing the root with subtree: true, we catch everything.
    const observer = new MutationObserver((mutations) => {
      const upsellContainer = this.querySelector('#cart-drawer-upsell-recommendations');

      // If container exists and is empty, fetch.
      if (upsellContainer && upsellContainer.innerHTML.trim() === '') {
        console.log('[AI Upsell] Observer detected empty container, triggering fetch');
        this.fetchUpsellRecommendations();
      }
    });

    observer.observe(this, { childList: true, subtree: true });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.fetchUpsellRecommendations();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.fetchUpsellRecommendations();
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  fetchUpsellRecommendations() {
    console.log('[AI Upsell] Fetch initiated');
    const upsellContainer = document.getElementById('cart-drawer-upsell-recommendations');
    if (!upsellContainer) {
      console.log('[AI Upsell] Container not found');
      return;
    }

    fetch(window.Shopify.routes.root + 'cart.js')
      .then(response => response.json())
      .then(cart => {
        console.log('[AI Upsell] Cart fetched:', cart);
        if (cart.items.length === 0) {
          console.log('[AI Upsell] Cart is empty');
          upsellContainer.innerHTML = '';
          const upsellWrapper = upsellContainer.closest('.cart-drawer-upsell');
          if (upsellWrapper) upsellWrapper.style.display = 'none';
          return;
        }

        const productId = cart.items[0].product_id;
        const limit = 10;
        const sectionId = 'cart-drawer';
        // Cart drawer renders the whole drawer, but we only need the fallback part which gets populated by recommendations
        const url = window.Shopify.routes.root + `recommendations/products?section_id=${sectionId}&product_id=${productId}&limit=${limit}`;
        console.log('[AI Upsell] Fetching recommendations from:', url);

        // Removed intent=related to allow default fallback
        fetch(url)
          .then(response => response.text())
          .then(text => {
            console.log('[AI Upsell] Recommendation response received (length):', text.length);
            const html = new DOMParser().parseFromString(text, 'text/html');
            // When fetching section_id=cart-drawer, the response is the whole drawer.
            // But inside snippets/cart-drawer.liquid, the recommendations loop runs.
            // We need to extract the loop's content.
            const recommendationsContainer = html.getElementById('cart-drawer-upsell-recommendations');

            const upsellWrapper = upsellContainer.closest('.cart-drawer-upsell');

            if (recommendationsContainer && recommendationsContainer.querySelector('.cart-drawer-upsell__item')) {
              console.log('[AI Upsell] Recommendations found in DOM');
              upsellContainer.innerHTML = recommendationsContainer.innerHTML;
              if (upsellWrapper) upsellWrapper.style.display = 'block';
            } else {
              console.log('[AI Upsell] No recommendations found in response DOM');
              upsellContainer.innerHTML = '';
              if (upsellWrapper) upsellWrapper.style.display = 'none';
            }
          })
          .catch(e => {
            console.error('[AI Upsell] Error fetching recommendations:', e);
          });
      });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
